<template>
  <div>
    <div class="content">
      <div id="page-builder" class="builder"></div>
    </div>
    <div class="overlay"></div>
    <div class="control-bar">
      <div class="control-buttons">
        <div
          v-if="$can('updateProjectItem')"
          class="control-button"
          @click="beeInstance.save()"
        >
          <i class="icon-check" />
          {{ $t('Sites_Form_PageBuilder_Button_Save') }}
        </div>
        <div class="control-button" @click="beeInstance.togglePreview()">
          <i class="icon-eye" />
          {{ $t('Sites_Form_PageBuilder_Button_Preview') }}
        </div>
        <div class="control-button" @click="beeInstance.toggleStructure()">
          <i class="icon-doc-text" />
          {{ $t('Sites_Form_PageBuilder_Button_Structure') }}
        </div>
        <div class="control-button" @click="confirmClose">
          <i class="icon-cancel" />
          {{ $t('Sites_Form_PageBuilder_Button_Discard') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BlankTemplate from '@/common/components/email/BlankTemplate'
import BeePlugin from '@mailupinc/bee-plugin'
import PageBuilderAuth from '@/common/components/microsite/PageBuilderAuth'
import { projectEndpoint } from '@/core/helpers'
import { RestApiPaginator } from '@/core/utils/RestApiPaginator'
import _ from 'lodash'
import { BeeLanguageEnum } from '@/common/enum/BeeLanguageEnum'

export default {
  name: 'PageBuilder',

  props: {
    value: {
      type: Object,
      required: true
    },
    onClose: {
      type: Function,
      required: true
    }
  },

  data() {
    return {
      uid: null,
      customAttributes: [],
      beeInstance: null,
      isChanged: false,
      fieldTypes: [
        'text',
        'number',
        'date',
        'text',
        'checkbox',
        'number',
        'date',
        'text'
      ],
      regex: /^[A-Za-z0-9_-]+$/,
      BeeLanguageEnum
    }
  },

  created() {
    new RestApiPaginator(this.fetchAttributes)
      .fetchAllPages()
      .then(data => {
        this.customAttributes = data.filter(attr => this.regex.test(attr.name))
        this.$nextTick(() => {
          this.loadBuilder()
        })
      })
      .catch(error => {
        this.$message({
          message: error.message,
          type: 'error'
        })
      })
  },

  beforeDestroy() {
    const el = document.getElementById('page-builder')
    if (el) {
      el.parentElement.removeChild(el)
    }
  },

  computed: {
    template() {
      if (!this.value.page_json) {
        return BlankTemplate
      }
      try {
        let content = JSON.parse(this.value.page_json)
        if (content) {
          return this.updateAttributes(content)
        }
      } catch (e) {
        return BlankTemplate
      }
    },
    fetchAttributes() {
      return projectEndpoint({
        method: 'get',
        url: `tags`,
        params: {
          sort: 'title',
          attribute_profile: 1
        }
      })
    },
    defaultForm() {
      let defaultForm = {
        structure: {
          title: 'Form title',
          fields: {
            'attr-email': { type: 'email', label: 'Email' },
            'attr-mobile_number': {
              type: 'tel',
              label: 'Mobile number',
              removeFromLayout: true
            },
            'attr-whatsapp_number': {
              type: 'tel',
              label: 'Whatsapp number',
              removeFromLayout: true
            },
            submit: {
              type: 'submit',
              label: ' ',
              attributes: { value: 'Submit', name: 'submit_button' },
              canBeRemovedFromLayout: false
            }
          },
          layout: [],
          attributes: {
            'accept-charset': 'UTF-8',
            action: this.actionUrl,
            method: 'post',
            novalidate: false,
            target: '_self'
          }
        }
      }
      const customFields = this.customAttributes.map(attr => ({
        [`attr-${attr.name}`]: {
          type: this.fieldTypes[attr.type] || 'text',
          label: attr.display_name || attr.name,
          removeFromLayout: true
        }
      }))

      defaultForm.structure.fields = Object.assign(
        {},
        ...customFields,
        defaultForm.structure.fields
      )

      defaultForm.structure.layout = [
        ...Object.keys(defaultForm.structure.fields).map(item => [item])
      ]

      return defaultForm
    },
    beeConfig() {
      return {
        uid: this.uid,
        container: 'page-builder',
        language: this.beeLanguage,
        defaultForm: { ...this.defaultForm },

        onSave: (jsonFile, htmlFile) => {
          this.value.page_json = jsonFile
          this.value.page_html = htmlFile
          this.isChanged = false
          this.onClose()
        },
        onChange: () => {
          this.isChanged = true
        },
        onError: errorMessage => {
          if (this.onError) {
            this.onError(errorMessage)
          }
        }
      }
    },
    actionUrl() {
      if (this.$store.state.app.domains.external_api) {
        return `https://${this.$store.state.app.domains.external_api}/api/sites/form-submission`
      }
      return '#'
    },
    beeLanguage() {
      let language = BeeLanguageEnum[this.$store.state.app.user.language]
      if (!language) {
        language = BeeLanguageEnum[this.$store.state.app.provider.language]
      }
      return language || BeeLanguageEnum['en']
    }
  },

  methods: {
    async loadBuilder() {
      PageBuilderAuth.getToken().then(token => {
        this.beeInstance = new BeePlugin(token)
        this.uid = token.uid
        this.beeInstance.start(this.beeConfig, this.template)
      })
    },
    confirmClose() {
      if (this.isChanged) {
        this.$msgbox({
          title: this.$t('Sites_Form_PageBuilder_Close_Title'),
          message: this.$t('Sites_Form_PageBuilder_Close_Desc'),
          showCancelButton: true,
          confirmButtonText: this.$t('Sites_Form_PageBuilder_Button_Confirm'),
          cancelButtonText: this.$t('Sites_Form_PageBuilder_Button_Cancel'),
          center: true
        }).then(() => {
          this.onClose()
        })
      } else {
        this.onClose()
      }
    },
    updateAttributes(content) {
      if (!_.has(content, 'page.rows')) {
        return content
      }

      content.page.rows.forEach((row, rowIndex) => {
        if (!row.hasOwnProperty('columns')) {
          return
        }
        row.columns.forEach((column, colIndex) => {
          if (!column.hasOwnProperty('modules')) {
            return
          }
          let modules = column.modules.map(module => {
            if (
              !_.has(module, 'descriptor.form.structure.fields') ||
              !_.has(module, 'descriptor.form.structure.layout')
            ) {
              return module
            }

            let fields = module.descriptor.form.structure.fields
            let layout = module.descriptor.form.structure.layout
            let newFields = _.cloneDeep(this.defaultForm.structure.fields)
            let newLayout = _.cloneDeep(this.defaultForm.structure.layout)

            // Set field settings (type, label, removeFromLayout)
            Object.keys(newFields).forEach(field => {
              if (fields.hasOwnProperty(field)) {
                newFields[field] = _.cloneDeep(fields[field])
              }
            })
            module.descriptor.form.structure.fields = newFields

            // Remove all deleted attributes from layout
            layout = layout.filter(item => {
              return !!newLayout.filter(arr => _.isEqual(arr, item)).length
            })
            // Add new fields into existing layout
            module.descriptor.form.structure.layout = _.uniqWith(
              [...layout, ...newLayout],
              _.isEqual
            )
            return module
          })

          content.page.rows[rowIndex].columns[colIndex].modules = modules
        })
      })

      return content
    }
  }
}
</script>
<style scoped>
.overlay {
  position: fixed;
  z-index: 1000000;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  background: #32373a;
}
.content {
  position: fixed;
  z-index: 1000001;
  top: 55px;
  left: 0;
  right: 0;
  bottom: 0;
}
.builder {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}
.control-bar {
  position: fixed;
  z-index: 1000002;
  top: 0;
  left: 0;
  right: 0;
  height: 55px;
  background: #505659;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.control-buttons {
  float: left;
}
.control-button {
  cursor: pointer;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  border-radius: 4px;
  -webkit-border-radius: 4px;
  -moz-border-radius: 4px;
  float: left;
  height: 55px;
  line-height: 55px;
  font-size: 14px;
  color: #ffffff;
  padding: 0 5px;
  margin-right: 20px;
}
.control-button:hover {
  opacity: 0.8;
}
.launcher {
  display: flex;
  align-items: center;
}
h1.launcher-button {
  display: inline-block;
}

>>> .vuedals {
  z-index: 10000000;
}

>>> .vuedal {
  max-height: 85vh;
  overflow: hidden;
}
</style>



// WEBPACK FOOTER //
// src/modules/sites/components/PageBuilder.vue